import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { invalidateReportsCache } from "@/lib/react-query/invalidateReportsCache";
import { useTranslation } from "react-i18next";

export interface AppointmentItemRow {
  id: string;
  appointment_id: string;
  item_id: string;
  qty: number;
  title_snapshot: string;
  created_at: string;
  updated_at: string;
  item?: {
    id: string;
    name: string;
    type?: string;
    sale_price?: number | null;
    track_by_batch?: boolean | null;
    track_by_expiry?: boolean | null;
  } | null;
}

export interface AppointmentItemInput {
  item_id: string;
  qty: number;
  title_snapshot: string;
}

export function useAppointmentItems(appointmentId: string | undefined) {
  return useQuery({
    queryKey: ["appointment-items", appointmentId],
    queryFn: async (): Promise<AppointmentItemRow[]> => {
      if (!appointmentId) return [];
      const { data, error } = await supabase
        .from("appointment_items")
        .select(
          `
          id,
          appointment_id,
          item_id,
          qty,
          title_snapshot,
          created_at,
          updated_at,
          item:items(id, name, type, sale_price, track_by_batch, track_by_expiry)
        `,
        )
        .eq("appointment_id", appointmentId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      return (data ?? []) as AppointmentItemRow[];
    },
    enabled: !!appointmentId,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useReplaceAppointmentItems() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("calendar");

  return useMutation({
    mutationFn: async ({
      appointmentId,
      items,
    }: {
      appointmentId: string;
      items: AppointmentItemInput[];
    }) => {
      if (!appointmentId) throw new Error(t("phase8.appointmentItems.errors.appointmentIdRequired"));

      const { error: deleteError } = await supabase
        .from("appointment_items")
        .delete()
        .eq("appointment_id", appointmentId);

      if (deleteError) throw deleteError;

      if (items.length > 0) {
        const payload = items.map((item) => ({
          appointment_id: appointmentId,
          item_id: item.item_id,
          qty: item.qty,
          title_snapshot: item.title_snapshot || "",
        }));

        const { error: insertError } = await supabase
          .from("appointment_items")
          .insert(payload);

        if (insertError) throw insertError;
      }

      return appointmentId;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["appointment-items", variables.appointmentId] });
      queryClient.invalidateQueries({ queryKey: ["appointment", variables.appointmentId] });
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      queryClient.invalidateQueries({ queryKey: ["finalized-appointments"] });
      invalidateReportsCache(queryClient);
      toast({
        title: t("phase8.appointmentItems.replacedTitle"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("phase8.appointmentItems.replaceErrorTitle"),
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useAddAppointmentItem() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("calendar");

  return useMutation({
    mutationFn: async ({
      appointmentId,
      item,
    }: {
      appointmentId: string;
      item: AppointmentItemInput;
    }) => {
      const { data, error } = await supabase
        .from("appointment_items")
        .insert({
          appointment_id: appointmentId,
          item_id: item.item_id,
          qty: item.qty,
          title_snapshot: item.title_snapshot || "",
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["appointment-items", variables.appointmentId] });
      queryClient.invalidateQueries({ queryKey: ["appointment", variables.appointmentId] });
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      queryClient.invalidateQueries({ queryKey: ["finalized-appointments"] });
      invalidateReportsCache(queryClient);
      toast({
        title: t("phase8.appointmentItems.addedTitle"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("phase8.appointmentItems.addErrorTitle"),
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useDeleteAppointmentItem() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("calendar");

  return useMutation({
    mutationFn: async ({
      itemId,
      appointmentId,
    }: {
      itemId: string;
      appointmentId: string;
    }) => {
      const { error } = await supabase
        .from("appointment_items")
        .delete()
        .eq("id", itemId);

      if (error) throw error;
      return itemId;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["appointment-items", variables.appointmentId] });
      queryClient.invalidateQueries({ queryKey: ["appointment", variables.appointmentId] });
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      queryClient.invalidateQueries({ queryKey: ["finalized-appointments"] });
      invalidateReportsCache(queryClient);
      toast({
        title: t("phase8.appointmentItems.deletedTitle"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("phase8.appointmentItems.deleteErrorTitle"),
        description: error.message,
        variant: "destructive",
      });
    },
  });
}
